/**
 * NotFoundPage Component - Pagina 404 personalizzata per STUDIO ROMA SERVICE
 * 
 * TESTING:
 * - URL: Aggiungi ?test=404 all'URL per testare (es: http://localhost:5173/?test=404)
 * - Keyboard: Premi Ctrl+Shift+4 per accedere rapidamente alla pagina 404
 * - Navigation: Usa onNavigateToPage('not-found') da qualsiasi componente
 */

import React from 'react';
import { Search, Phone, Shield, Bug, Users, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CONTACT_INFO } from '@/utils/constants/app';

import Link from 'next/link';

interface NotFoundPageProps {
  onBackToHome?: () => void;
  onNavigateToPage?: (page: string) => void;
}

export function NotFoundPage({ onBackToHome, onNavigateToPage }: Readonly<NotFoundPageProps>) {
  const suggestedPages = [
    {
      icon: <Bug className="w-6 h-6" />,
      title: "Disinfestazione",
      description: "Servizi professionali contro tutti i tipi di insetti",
      page: "disinfestazione"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Derattizzazione", 
      description: "Controllo e eliminazione di topi e ratti",
      page: "deratizzazione"
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Chi Siamo",
      description: "Scopri di più sulla nostra azienda e i nostri servizi",
      page: "contacts"
    }
  ];

  const handlePageNavigation = (page: string) => {
    if (onNavigateToPage) {
      onNavigateToPage(page);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
      <div className="max-w-4xl mx-auto text-center">
        
        {/* Main 404 Content */}
        <div className="mb-12">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-32 h-32 bg-green-100 rounded-full mb-6">
              <Search className="w-16 h-16 text-green-600" />
            </div>
            
            <h1 className="text-6xl md:text-8xl font-bold text-gray-800 mb-4">
              4<span className="text-green-600">0</span>4
            </h1>
            
            <h2 className="text-2xl md:text-3xl font-semibold text-gray-700 mb-4">
              Pagina Non Trovata
            </h2>
            
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Ci dispiace, ma la pagina che stai cercando non esiste o è stata spostata. 
              Puoi tornare alla homepage o esplorare i nostri servizi qui sotto.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            {onBackToHome ? (
              <Button 
                onClick={onBackToHome}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg text-lg font-medium inline-flex items-center"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Torna alla Homepage
              </Button>
            ) : (
              <Link href="/" className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg text-lg font-medium inline-flex items-center">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Torna alla Homepage
              </Link>
            )}
            
            <Button 
              variant="outline"
              onClick={() => window.location.href = `tel:${CONTACT_INFO.MAIN_PHONE}`}
              className="border-green-600 text-green-600 hover:bg-green-50 px-8 py-3 rounded-lg text-lg font-medium inline-flex items-center"
            >
              <Phone className="w-5 h-5 mr-2" />
              Chiama Ora: {CONTACT_INFO.MAIN_PHONE}
            </Button>
          </div>
        </div>

        {/* Suggested Pages */}
        <div className="mb-12">
          <h3 className="text-xl font-semibold text-gray-800 mb-6">
            Potrebbero interessarti anche:
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {suggestedPages.map((item) => (
              <Card 
        key={item.title}
                className="p-6 hover:shadow-lg transition-shadow cursor-pointer border border-gray-200 hover:border-green-300"
                onClick={() => handlePageNavigation(item.page)}
              >
                <div className="flex flex-col items-center text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mb-4 text-green-600">
                    {item.icon}
                  </div>
                  
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">
                    {item.title}
                  </h4>
                  
                  <p className="text-gray-600 text-sm">
                    {item.description}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        </div>

    
       
      </div>
    </div>
  );
}